/**
 * SmartTask V2 - Drawing Viewer Modal
 * 
 * Full-screen viewing of engineering drawings, blueprints, and technical diagrams.
 * Supports pinch-to-zoom and pan gestures.
 */

'use client';

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw, Download, Maximize2, FileText, ImageIcon, Loader2, MessageSquare } from 'lucide-react';
import CommentsSection from '@/components/work-items/CommentsSection';
import { authService } from '@/lib/authService';
import {
  getPublishedDrawingsRegisterCache,
  setPublishedDrawingsRegisterCache,
} from '@/lib/smartTask/publishedDrawingsRegisterCache';
import {
  getSmartTaskViewportBottomContentStyle,
  SMARTTASK_TABLET_PORTAL_MEDIA_FRAME_CLASS,
  useSmartTaskViewport,
} from '../viewport';
import { resolveDrawingFileUrl } from '@/components/shared/DrawingManagerV3/drawingFileUrl';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;

function touchDistance(a: { clientX: number; clientY: number }, b: { clientX: number; clientY: number }): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy) || 1;
}

interface DrawingAsset {
  url: string;
  title?: string;
  mimeType?: string;
}

interface DrawingViewerProps {
  isOpen: boolean;
  drawingUrl?: string;
  drawingTitle?: string;
  drawings?: Array<string | Record<string, unknown>>;
  onClose: () => void;
  /**
   * When set (e.g. advanced-manufacturing), loads **published** drawings from the operations
   * drawing register for this work item and prepends them to any legacy custom-field URLs.
   */
  operationsPublishedDrawingsForWorkItemId?: string | null;
  /** Work item id for work-item comments when register id differs or drawings come from legacy URLs only. */
  commentsWorkItemId?: string | null;
}

function inferMimeType(url: string, providedMimeType?: string): string | undefined {
  if (providedMimeType) {
    return providedMimeType;
  }

  const normalized = url.split('?')[0].toLowerCase();
  if (normalized.endsWith('.pdf')) return 'application/pdf';
  if (normalized.endsWith('.svg')) return 'image/svg+xml';
  if (normalized.endsWith('.png')) return 'image/png';
  if (normalized.endsWith('.jpg') || normalized.endsWith('.jpeg')) return 'image/jpeg';
  if (normalized.endsWith('.webp')) return 'image/webp';
  if (normalized.endsWith('.gif')) return 'image/gif';
  return undefined;
}

function normalizeDrawingAsset(entry: string | Record<string, unknown> | null | undefined, fallbackTitle?: string): DrawingAsset | null {
  if (typeof entry === 'string') {
    const url = entry.trim();
    if (!url) return null;
    return {
      url,
      title: fallbackTitle,
      mimeType: inferMimeType(url),
    };
  }

  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const url = [entry.url, entry.href, entry.drawing_url, entry.file_url, entry.preview_url]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    ?.trim();

  if (!url) {
    return null;
  }

  const title = [entry.title, entry.name, entry.label, entry.filename, entry.drawing_name, entry.original_filename]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    ?.trim();

  const mimeType = [entry.mimeType, entry.mime_type, entry.contentType, entry.content_type, entry.mime]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    ?.trim();

  return {
    url,
    title: title || fallbackTitle,
    mimeType: inferMimeType(url, mimeType),
  };
}

function mapRegisterDrawingRowToAsset(row: unknown): DrawingAsset | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const fileUrl = r.file_url;
  const thumbUrl = r.thumbnail_url;
  const urlRaw =
    (typeof fileUrl === 'string' && fileUrl.trim() ? fileUrl : null)
    ?? (typeof thumbUrl === 'string' && thumbUrl.trim() ? thumbUrl : null);
  if (!urlRaw) return null;
  const url = urlRaw.trim();
  const title =
    [r.drawing_number, r.drawing_name, r.title, r.original_filename]
      .find((v) => typeof v === 'string' && v.trim()) as string | undefined;
  const mimeRaw = [r.mime_type, r.mimeType, r.file_type]
    .find((v) => typeof v === 'string' && v.trim()) as string | undefined;
  const normalizedMime = mimeRaw?.includes('/')
    ? mimeRaw.trim()
    : mimeRaw?.toLowerCase() === 'pdf'
      ? 'application/pdf'
      : undefined;
  return {
    url,
    title: title?.trim(),
    mimeType: inferMimeType(url, normalizedMime),
  };
}

export default function DrawingViewer({
  isOpen,
  drawingUrl,
  drawingTitle,
  drawings = [],
  onClose,
  operationsPublishedDrawingsForWorkItemId = null,
  commentsWorkItemId: commentsWorkItemIdProp = null,
}: DrawingViewerProps) {
  const { isTabletPortal } = useSmartTaskViewport();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [registerAssets, setRegisterAssets] = useState<DrawingAsset[]>([]);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [activeRasterError, setActiveRasterError] = useState(false);
  const [viewerSegment, setViewerSegment] = useState<'drawing' | 'comments'>('drawing');
  const dragStart = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(1);
  const positionRef = useRef({ x: 0, y: 0 });
  const pinchRef = useRef<{ initialDistance: number; initialZoom: number } | null>(null);
  zoomRef.current = zoom;
  positionRef.current = position;

  useEffect(() => {
    if (!isOpen) {
      setRegisterAssets([]);
      setRegisterLoading(false);
      setRegisterError(null);
      setViewerSegment('drawing');
      return;
    }

    const wid = operationsPublishedDrawingsForWorkItemId?.trim();
    if (!wid) {
      setRegisterAssets([]);
      setRegisterLoading(false);
      setRegisterError(null);
      return;
    }

    let cancelled = false;
    const cached = getPublishedDrawingsRegisterCache(wid);
    if (cached) {
      setRegisterAssets(
        cached.map((c) => ({ url: c.url, title: c.title, mimeType: c.mimeType })),
      );
      setRegisterLoading(false);
      setRegisterError(null);
      return undefined;
    }

    setRegisterLoading(true);
    setRegisterError(null);

    (async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          if (!cancelled) {
            setRegisterAssets([]);
            setRegisterError('Sign in required to load issued drawings.');
          }
          return;
        }
        const params = new URLSearchParams({ work_item_id: wid, published_only: 'true' });
        const res = await fetch(`/api/v1/drawings?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: { success?: boolean; drawings?: unknown[]; error?: string } = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !json?.success || !Array.isArray(json.drawings)) {
          setRegisterAssets([]);
          setRegisterError(json?.error || `Could not load issued drawings (${res.status}).`);
          return;
        }
        const assets = json.drawings
          .map(mapRegisterDrawingRowToAsset)
          .filter((a): a is DrawingAsset => Boolean(a));
        setRegisterAssets(assets);
        setPublishedDrawingsRegisterCache(
          wid,
          assets.map((a) => ({ url: a.url, title: a.title, mimeType: a.mimeType })),
        );
      } catch {
        if (!cancelled) {
          setRegisterAssets([]);
          setRegisterError('Network error loading issued drawings.');
        }
      } finally {
        if (!cancelled) setRegisterLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, operationsPublishedDrawingsForWorkItemId]);

  const drawingAssets = useMemo(() => {
    const fromRegister = registerAssets;
    const fromLegacy = drawings
      .map((entry) => normalizeDrawingAsset(entry, drawingTitle))
      .filter((entry): entry is DrawingAsset => Boolean(entry));

    const merged = [...fromRegister, ...fromLegacy];
    if (merged.length > 0) {
      return merged;
    }

    const fallback = normalizeDrawingAsset(drawingUrl || null, drawingTitle);
    return fallback ? [fallback] : [];
  }, [drawingTitle, drawingUrl, drawings, registerAssets]);

  const resolvedActiveIndex = activeIndex >= 0 && activeIndex < drawingAssets.length ? activeIndex : 0;
  const activeDrawing = drawingAssets[resolvedActiveIndex] || drawingAssets[0] || null;
  const activeDrawingMimeType = activeDrawing?.mimeType || '';
  const isPdfDrawing = activeDrawingMimeType === 'application/pdf';
  const isImageDrawing = activeDrawingMimeType.startsWith('image/');

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, MAX_ZOOM));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, MIN_ZOOM));
  const handleReset = () => { setZoom(1); setRotation(0); setPosition({ x: 0, y: 0 }); };
  const handleRotate = () => setRotation(r => (r + 90) % 360);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isPdfDrawing) return;
    if (e.button !== 0) return;
    setDragging(true);
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  }, [position, isPdfDrawing]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPdfDrawing) return;
    if (!dragging) return;
    setPosition({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  }, [dragging, isPdfDrawing]);

  const handleMouseUp = useCallback(() => {
    if (!isPdfDrawing) setDragging(false);
  }, [isPdfDrawing]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (isPdfDrawing && activeDrawing) return;

      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left - rect.width / 2;
      const my = e.clientY - rect.top - rect.height / 2;
      const prevZ = zoomRef.current;
      const nextZ = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prevZ * Math.exp(-e.deltaY * 0.0015)));
      const ratio = nextZ / prevZ;
      const prevP = positionRef.current;
      setZoom(nextZ);
      setPosition({
        x: (prevP.x - mx) * ratio + mx,
        y: (prevP.y - my) * ratio + my,
      });
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isPdfDrawing, activeDrawing]);

  useEffect(() => {
    setActiveIndex(0);
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
    setActiveRasterError(false);
  }, [operationsPublishedDrawingsForWorkItemId, registerAssets.length, drawings.length, drawingUrl]);

  useEffect(() => {
    setActiveRasterError(false);
  }, [resolvedActiveIndex, activeDrawing?.url]);

  const displayImageUrl = activeDrawing?.url ? resolveDrawingFileUrl(activeDrawing.url) : '';
  const resolvedCommentsWorkItemId =
    commentsWorkItemIdProp?.trim() || operationsPublishedDrawingsForWorkItemId?.trim() || '';

  if (!isOpen) return null;

  return (
    <div data-tour-id="smarttask-drawing-viewer" className="fixed inset-0 z-[65] bg-black" style={getSmartTaskViewportBottomContentStyle({ minimumBottom: '0px' })}>
      <div className={isTabletPortal ? `${SMARTTASK_TABLET_PORTAL_MEDIA_FRAME_CLASS} bg-black flex min-h-0 flex-col` : 'flex h-full min-h-0 flex-col'}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gray-900/90 text-white z-10" style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold truncate">{activeDrawing?.title || drawingTitle || 'Drawing Viewer'}</h2>
          <p className="text-xs text-gray-400">
            {registerError
              ? registerError
              : registerLoading
                ? 'Loading issued drawings…'
                : `${Math.round(zoom * 100)}% • ${rotation}°`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <div className="mr-1 flex rounded-lg bg-white/10 p-0.5">
            <button
              type="button"
              onClick={() => setViewerSegment('drawing')}
              className={`rounded-md px-2 py-1 text-xs font-medium ${
                viewerSegment === 'drawing' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Drawing
            </button>
            <button
              type="button"
              onClick={() => setViewerSegment('comments')}
              className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${
                viewerSegment === 'comments' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <MessageSquare className="h-3.5 w-3.5" aria-hidden />
              Comments
            </button>
          </div>
          {registerLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden />
          )}
          <button onClick={handleZoomOut} className="p-2 hover:bg-white/10 rounded-lg">
            <ZoomOut className="w-4 h-4" />
          </button>
          <button onClick={handleZoomIn} className="p-2 hover:bg-white/10 rounded-lg">
            <ZoomIn className="w-4 h-4" />
          </button>
          <button onClick={handleRotate} className="p-2 hover:bg-white/10 rounded-lg">
            <RotateCcw className="w-4 h-4" />
          </button>
          <button onClick={handleReset} className="p-2 hover:bg-white/10 rounded-lg">
            <Maximize2 className="w-4 h-4" />
          </button>
          {activeDrawing?.url && (
            <a href={activeDrawing.url} download className="p-2 hover:bg-white/10 rounded-lg">
              <Download className="w-4 h-4" />
            </a>
          )}
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg ml-2">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        className={`flex min-h-0 flex-1 overflow-hidden ${
          viewerSegment === 'comments' ? 'flex-col lg:flex-row' : 'flex-col'
        }`}
      >
        <div
          className={`flex min-h-0 flex-col ${viewerSegment === 'comments' ? 'min-w-0 flex-1' : 'flex-1'}`}
        >
      {drawingAssets.length > 1 && (
        <div className="flex gap-2 overflow-x-auto border-b border-white/10 bg-gray-950 px-3 py-2 text-white">
          {drawingAssets.map((asset, index) => {
            const assetIsPdf = (asset.mimeType || '').startsWith('application/pdf');
            const AssetIcon = assetIsPdf ? FileText : ImageIcon;

            return (
              <button
                key={`${asset.url}-${index}`}
                type="button"
                onClick={() => {
                  setActiveIndex(index);
                  handleReset();
                }}
                className={`inline-flex min-w-0 items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                  index === resolvedActiveIndex
                    ? 'border-blue-400 bg-blue-500/20 text-blue-100'
                    : 'border-white/10 bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                <AssetIcon className="h-4 w-4 shrink-0" />
                <span className="max-w-[12rem] truncate">{asset.title || `Drawing ${index + 1}`}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Drawing area */}
      <div
        ref={containerRef}
        className="min-h-0 flex-1 overflow-hidden cursor-grab active:cursor-grabbing touch-none bg-gray-900"
        style={{ touchAction: isPdfDrawing ? 'auto' : 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={(e) => {
          if (isPdfDrawing) return;
          if (e.touches.length === 2) {
            pinchRef.current = {
              initialDistance: touchDistance(e.touches[0], e.touches[1]),
              initialZoom: zoomRef.current,
            };
            setDragging(false);
            return;
          }
          if (e.touches.length === 1) {
            pinchRef.current = null;
            const touch = e.touches[0];
            setDragging(true);
            dragStart.current = { x: touch.clientX - position.x, y: touch.clientY - position.y };
          }
        }}
        onTouchMove={(e) => {
          if (isPdfDrawing) return;
          if (e.touches.length === 2 && pinchRef.current) {
            e.preventDefault();
            const d = touchDistance(e.touches[0], e.touches[1]);
            const ratio = d / pinchRef.current.initialDistance;
            setZoom(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchRef.current.initialZoom * ratio)));
            return;
          }
          if (!dragging || e.touches.length !== 1) return;
          const touch = e.touches[0];
          setPosition({ x: touch.clientX - dragStart.current.x, y: touch.clientY - dragStart.current.y });
        }}
        onTouchEnd={(e) => {
          if (isPdfDrawing) return;
          if (e.touches.length < 2) pinchRef.current = null;
          if (e.touches.length === 0) setDragging(false);
        }}
      >
        {activeDrawing ? (
          isPdfDrawing ? (
            <iframe
              src={activeDrawing.url}
              title={activeDrawing.title || drawingTitle || 'Drawing'}
              className="h-full w-full border-0"
            />
          ) : isImageDrawing ? (
            activeRasterError ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-gray-300">
                <ImageIcon className="h-10 w-10 text-gray-500" />
                <p className="text-sm font-medium">Image failed to load</p>
                <p className="text-xs text-gray-500">
                  The preview link may have expired. Try download or open in a new tab.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <a
                    href={activeDrawing.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
                  >
                    Open
                  </a>
                  <a
                    href={activeDrawing.url}
                    download
                    className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-white/10"
                  >
                    Download
                  </a>
                </div>
              </div>
            ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom}) rotate(${rotation}deg)`,
              transformOrigin: 'center center',
              transition: dragging ? 'none' : 'transform 0.2s ease',
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={displayImageUrl}
              alt={activeDrawing.title || drawingTitle || 'Drawing'}
              className="max-w-none select-none pointer-events-none"
              draggable={false}
              onError={() => setActiveRasterError(true)}
            />
          </div>
            )
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center text-gray-300">
              <FileText className="h-10 w-10 text-gray-500" />
              <div>
                <p className="text-sm font-medium">Preview not available for this drawing format</p>
                <p className="mt-1 text-xs text-gray-500">Open or download the document to inspect it in full detail.</p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={activeDrawing.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
                >
                  Open Document
                </a>
                <a
                  href={activeDrawing.url}
                  download
                  className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-white/10"
                >
                  Download
                </a>
              </div>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            No drawing available
          </div>
        )}
      </div>
        </div>

        {viewerSegment === 'comments' && (
          <div className="flex max-h-[45vh] w-full shrink-0 flex-col border-t border-white/10 bg-gray-950 lg:max-h-none lg:w-[22rem] lg:border-l lg:border-t-0">
            <div className="border-b border-white/10 px-3 py-2">
              <p className="text-xs font-semibold text-gray-200">Work item comments</p>
              <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
                {resolvedCommentsWorkItemId
                  ? 'Same thread as on the work item detail. Not tied to a single sheet.'
                  : 'Open from a task with a work item, or pass a work item id, to load comments.'}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {resolvedCommentsWorkItemId ? (
                <CommentsSection
                  workItemId={resolvedCommentsWorkItemId}
                  isActive={viewerSegment === 'comments'}
                  showInternalToggle
                  defaultInternal
                  composerPresentation="inline"
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
